import { MouseEvent } from 'react';

import Avatar from '@mui/material/Avatar';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/system';
import { useTranslation } from 'react-i18next';

import TicketCard from 'src/common/components/styled/TicketCard';
import TicketCardEditDeleteFooter from 'src/common/components/styled/TicketCardEditDeleteFooter';
import { LanguageKeys } from 'src/common/constants/languages';
import { getTrackMapDisplayName } from 'src/helpers/trackMapDisplay';
import { ITrackMap } from 'src/interfaces/TrackMap';
import useSystemOptionsStore from 'src/stores/systemOptionsStore';

import { getTrackMapDistanceName } from '../tables/trackMapTableCells';

interface Props {
  trackMap: ITrackMap;
  onDeleteTrackMap: (trackMapId: string, e?: MouseEvent<unknown>) => void;
  onEditTrackMap: (trackMapId: string, e?: MouseEvent<unknown>) => void;
}

const TrackMapCard = ({
  trackMap: { distance: distanceId, fileName, id, i18n, levelDiff, name, published, route, time, totalDistance },
  onDeleteTrackMap,
  onEditTrackMap,
}: Props) => {
  const {
    t,
    i18n: { resolvedLanguage },
  } = useTranslation();
  const currentLang = resolvedLanguage as LanguageKeys;
  const { palette } = useTheme();
  const { distances, distancesById } = useSystemOptionsStore(systemOptionsState => systemOptionsState);

  const distanceName = getTrackMapDistanceName(distanceId, currentLang, distances, distancesById);
  const displayName = getTrackMapDisplayName({ i18n, name }, currentLang);
  const avatarLabel = distanceName.replace(/\s+/g, '\u00a0');

  return (
    <TicketCard
      eyebrow={t('drawer.trackMaps')}
      title={displayName}
      description={route.i18n[currentLang]}
      avatar={
        <Avatar
          sx={{
            background: `linear-gradient(135deg, ${palette.success.dark} 0%, ${palette.success.light} 100%)`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '0.65rem !important',
            fontWeight: 700,
            lineHeight: 1,
            textAlign: 'center',
            whiteSpace: 'nowrap',
          }}
          aria-label={distanceName}
        >
          {avatarLabel}
        </Avatar>
      }
      footer={
        <TicketCardEditDeleteFooter
          popupId={`track-map-tools-${id}`}
          optionsAriaLabel={t('system.trackMaps.optionsLabel')}
          editLabel={t('system.trackMaps.editTrackMap')}
          deleteLabel={t('system.trackMaps.deleteTrackMap')}
          onEdit={e => onEditTrackMap(id, e)}
          onDelete={e => onDeleteTrackMap(id, e)}
        />
      }
      badges={[
        {
          label:
            published !== false
              ? t('system.sponsors.published')
              : t('system.sponsors.unpublished'),
          highlighted: published !== false,
        },
        { label: distanceName, highlighted: true },
        {
          label: t('system.trackMaps.totalDistance', { dist: totalDistance }),
        },
        { label: t('timers.times.hour', { count: time }) },
      ]}
    >
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
        {t('system.trackMaps.level', { pos: levelDiff.pos, neg: levelDiff.neg })}
        {' · '}
        {fileName || '-'}
      </Typography>
    </TicketCard>
  );
};

export default TrackMapCard;
